import React, { useState, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import './TestDashboard.css';
import { DEFAULT_SERVERS, getDefaultApiUrl } from './apiConfig';

const STORAGE_KEY = 'sandhi_vikruti_test_cases';
const SERVER_URL_KEY = 'sandhi_vikruti_test_server_url';
const BATCH_COUNTER_KEY = 'sandhi_vikruti_test_batch_counter';

const SHAKHA_OPTIONS = [
  { value: 'तैत्तिरीय', label: 'तैत्तिरीय (Taittiriya)', veda: 'Yajur' },
  { value: 'माध्यन्दिन', label: 'माध्यन्दिन (Madhyandina)', veda: 'Yajur' },
  { value: 'शाकल', label: 'शाकल (Shakala)', veda: 'Rig' },
  { value: 'शौनक', label: 'शौनक (Shaunaka)', veda: 'Atharva' },
  { value: 'काण्व', label: 'काण्व (Kanva)', veda: 'Yajur' },
  { value: 'कौथुम', label: 'कौथुम (Kauthuma)', veda: 'Sama' },
  { value: 'रानायनीय', label: 'रानायनीय (Ranayaniya)', veda: 'Sama' },
];

const SandhiVikrutiTest = () => {
  // Server URL state
  const [serverUrl, setServerUrl] = useState(() => {
    const saved = localStorage.getItem(SERVER_URL_KEY);
    if (saved) return saved;
    return getDefaultApiUrl();
  });
  const [customServerUrl, setCustomServerUrl] = useState('');

  // Test cases state
  const [testCases, setTestCases] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved test cases:', e);
      }
    }
    return [createEmptyTestCase(1)];
  });

  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [lastSubmittedId, setLastSubmittedId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimerRef = React.useRef(null);
  const [lastSubmission, setLastSubmission] = useState(() => {
    const saved = localStorage.getItem('sandhi_vikruti_last_submission');
    return saved ? JSON.parse(saved) : null;
  });

  // Get current batch counter (without incrementing)
  const getCurrentBatchNumber = () => {
    return parseInt(localStorage.getItem(BATCH_COUNTER_KEY) || '0', 10);
  };

  const showToast = (message, type = 'success', duration = 4000, links = null) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type, links });
    if (duration > 0) {
      toastTimerRef.current = setTimeout(() => setToast(null), duration);
    }
  };

  function createEmptyTestCase(slNo) {
    return {
      id: Date.now() + Math.random(),
      slNo,
      input: '',
      userName: '',
      vedaShakha: 'तैत्तिरीय',
      showUdattaPrachaya: true,
      apadaantam: false,
      // Vikruti outputs
      kramaOutput: '',
      jataOutput: '',
      kramaOk: null,
      jataOk: null,
      kramaCorrection: '',
      jataCorrection: '',
      // Common
      scholarComment: '',
      submitted: false
    };
  }

  const saveTestCases = useCallback((newCases) => {
    setTestCases(newCases);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newCases));
  }, []);

  const handleServerChange = (url) => {
    setServerUrl(url);
    localStorage.setItem(SERVER_URL_KEY, url);
  };

  const addTestCase = () => {
    const newSlNo = testCases.length > 0 ? Math.max(...testCases.map(tc => tc.slNo)) + 1 : 1;
    saveTestCases([...testCases, createEmptyTestCase(newSlNo)]);
  };

  const updateTestCase = (id, field, value) => {
    saveTestCases(testCases.map(tc =>
      tc.id === id ? { ...tc, [field]: value } : tc
    ));
  };

  // Call vikrutipaatham API
  const callVikrutiApi = async (inputText, type, vedaShakha) => {
    const requestBody = {
      input: inputText,
      type: type
    };
    if (vedaShakha) {
      requestBody.vedshaakha = vedaShakha;
    }
    const response = await fetch(`${serverUrl}/v2/vikrutipaatham`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    if (result.data) {
      return Array.isArray(result.data) ? result.data.join('\n') : String(result.data);
    }
    return '';
  };

  // Process a single test case - calls Vikruti APIs (Krama/Jata)
  const processTestCase = async (testCase) => {
    if (!testCase.input.trim()) return testCase;

    setProcessingId(testCase.id);

    try {
      const [kramaResult, jataResult] = await Promise.all([
        callVikrutiApi(testCase.input, 'krama', testCase.vedaShakha).catch(e => `Error: ${e.message}`),
        callVikrutiApi(testCase.input, 'jata', testCase.vedaShakha).catch(e => `Error: ${e.message}`)
      ]);

      return {
        ...testCase,
        kramaOutput: kramaResult,
        jataOutput: jataResult,
        kramaOk: null,
        jataOk: null
      };
    } finally {
      setProcessingId(null);
    }
  };

  const processAllTestCases = async () => {
    setLoading(true);
    try {
      const processedCases = [];
      for (const testCase of testCases) {
        const processed = await processTestCase(testCase);
        processedCases.push(processed);
      }
      saveTestCases(processedCases);
    } finally {
      setLoading(false);
    }
  };

  const processSingleRow = async (testCase) => {
    const processed = await processTestCase(testCase);
    saveTestCases(testCases.map(tc => tc.id === testCase.id ? processed : tc));
  };

  const exportTestCases = () => {
    const exportData = {
      version: 1,
      type: 'sandhi-vikruti-test',
      exportDate: new Date().toISOString(),
      serverUrl,
      testCases: testCases.map(tc => ({
        slNo: tc.slNo,
        input: tc.input,
        userName: tc.userName,
        vedaShakha: tc.vedaShakha,
        showUdattaPrachaya: tc.showUdattaPrachaya,
        apadaantam: tc.apadaantam,
        kramaOutput: tc.kramaOutput,
        jataOutput: tc.jataOutput,
        kramaOk: tc.kramaOk,
        jataOk: tc.jataOk,
        kramaCorrection: tc.kramaCorrection,
        jataCorrection: tc.jataCorrection,
        scholarComment: tc.scholarComment,
        submitted: tc.submitted
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sandhi-vikruti-test-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importTestCases = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.testCases && Array.isArray(data.testCases)) {
          const importedCases = data.testCases.map((tc, index) => ({
            id: Date.now() + index,
            slNo: tc.slNo || index + 1,
            input: tc.input || '',
            userName: tc.userName || '',
            vedaShakha: tc.vedaShakha || 'तैत्तिरीय',
            showUdattaPrachaya: tc.showUdattaPrachaya !== undefined ? tc.showUdattaPrachaya : true,
            apadaantam: tc.apadaantam || false,
            kramaOutput: tc.kramaOutput || '',
            jataOutput: tc.jataOutput || '',
            kramaOk: tc.kramaOk || null,
            jataOk: tc.jataOk || null,
            kramaCorrection: tc.kramaCorrection || '',
            jataCorrection: tc.jataCorrection || '',
            scholarComment: tc.scholarComment || '',
            submitted: tc.submitted || false
          }));
          saveTestCases(importedCases);
        }
      } catch (err) {
        console.error('Failed to import test cases:', err);
        showToast('Failed to import test cases. Invalid JSON format.', 'error');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const clearAll = () => {
    if (window.confirm('Clear all test cases?')) {
      saveTestCases([createEmptyTestCase(1)]);
    }
  };

  const handleScholarNote = (tc) => {
    if (showNoteModal === tc.id) {
      setShowNoteModal(null);
    } else {
      setShowNoteModal(tc.id);
    }
  };

  const submitTestCase = (tc) => {
    if (!tc.kramaOutput && !tc.jataOutput) {
      showToast('Please process the test case first before submitting.', 'warn');
      return;
    }
    if (tc.kramaOk === null && tc.jataOk === null) {
      showToast('Please mark at least one output as Yes or No before submitting.', 'warn');
      return;
    }

    saveTestCases(testCases.map(t =>
      t.id === tc.id ? { ...t, submitted: true } : t
    ));
    setLastSubmittedId(tc.id);
  };

  const addRowAfterSubmit = () => {
    addTestCase();
    setLastSubmittedId(null);
  };

  const dismissSubmitConfirm = () => {
    setLastSubmittedId(null);
  };

  const filteredTestCases = activeTab === 'failed'
    ? testCases.filter(tc => tc.kramaOk === 'no' || tc.jataOk === 'no')
    : activeTab === 'report'
    ? testCases.filter(tc => tc.submitted)
    : testCases;

  const submittedTestCases = testCases.filter(tc => tc.submitted);
  const failedTestCases = testCases.filter(tc => tc.kramaOk === 'no' || tc.jataOk === 'no');

  const getVedaFromShakha = (shakha) => {
    const vedaMap = {
      'तैत्तिरीय': 'Yajurveda',
      'माध्यन्दिन': 'Yajurveda',
      'शाकल': 'Rigveda',
      'शौनक': 'Atharvaveda',
      'काण्व': 'Yajurveda',
      'कौथुम': 'Samaveda',
      'रानायनीय': 'Samaveda'
    };
    return vedaMap[shakha] || 'Yajurveda';
  };

  // Get next batch number
  const getNextBatchNumber = () => {
    const current = parseInt(localStorage.getItem(BATCH_COUNTER_KEY) || '0', 10);
    const next = current + 1;
    localStorage.setItem(BATCH_COUNTER_KEY, String(next));
    return next;
  };

  // Build batch payload from all test cases (both passed and failed)
  const buildBatchPayload = () => {
    const batchNumber = getNextBatchNumber();
    const timestamp = new Date().toISOString();

    const reports = [];
    testCases.forEach(tc => {
      // Krama report
      if (tc.kramaOutput && !tc.kramaOutput.startsWith('Error')) {
        reports.push({
          sl_no: tc.slNo,
          input: tc.input,
          veda: getVedaFromShakha(tc.vedaShakha),
          veda_shakha: tc.vedaShakha,
          user_name: tc.userName || 'Anonymous',
          format_type: 'krama',
          actual_output: tc.kramaOutput,
          expected_output: tc.kramaCorrection || '',
          status: tc.kramaOk,
          error_type: tc.kramaOk === 'no' ? 'mismatch' : 'passed',
          notes: tc.scholarComment || ''
        });
      }
      // Jata report
      if (tc.jataOutput && !tc.jataOutput.startsWith('Error')) {
        reports.push({
          sl_no: tc.slNo,
          input: tc.input,
          veda: getVedaFromShakha(tc.vedaShakha),
          veda_shakha: tc.vedaShakha,
          user_name: tc.userName || 'Anonymous',
          format_type: 'jata',
          actual_output: tc.jataOutput,
          expected_output: tc.jataCorrection || '',
          status: tc.jataOk,
          error_type: tc.jataOk === 'no' ? 'mismatch' : 'passed',
          notes: tc.scholarComment || ''
        });
      }
    });

    return {
      batch_number: batchNumber,
      timestamp,
      total_reports: reports.length,
      report_path: 'docs/test_results/failed_report_1',
      reports
    };
  };

  // Save batch JSON locally
  const saveBatchLocally = (batchPayload) => {
    const blob = new Blob([JSON.stringify(batchPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sandhi_vikruti_report_batch_${batchPayload.batch_number}_${batchPayload.timestamp.split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Send all test results to server
  // API: POST /v2/testcases/failed/batch with report_path for storage location
  const sendAllToServer = async () => {
    const validTestCases = testCases.filter(tc =>
      (tc.kramaOutput && !tc.kramaOutput.startsWith('Error')) ||
      (tc.jataOutput && !tc.jataOutput.startsWith('Error'))
    );

    if (validTestCases.length === 0) {
      showToast('No test results to send. Please process test cases first.', 'warn');
      return;
    }

    const batchPayload = buildBatchPayload();

    try {
      const response = await fetch(`${serverUrl}/v2/testcases/failed/batch`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batchPayload)
      });

      if (response.ok) {
        const result = await response.json();
        const issueLinks = (result.results || [])
          .filter(r => r.github_issue_number && r.github_issue_url)
          .map(r => ({ label: `#${r.github_issue_number}`, url: r.github_issue_url }));

        // Save last submission info
        const submissionInfo = {
          serial: batchPayload.batch_number,
          timestamp: batchPayload.timestamp,
          date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
          time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          reportCount: result.total_processed || batchPayload.total_reports,
          path: 'vedictools-server/docs/test_results'
        };
        setLastSubmission(submissionInfo);
        localStorage.setItem('sandhi_vikruti_last_submission', JSON.stringify(submissionInfo));

        const msg = `Batch #${batchPayload.batch_number}: ${result.total_processed || batchPayload.total_reports} report(s) saved to server.`;
        showToast(msg, 'success', 0, issueLinks.length > 0 ? issueLinks : null);
        saveBatchLocally(batchPayload);
      } else {
        const errorBody = await response.text().catch(() => '');
        console.error(`Failed to submit batch: HTTP ${response.status}: ${errorBody}`);
        showToast(`Server error (${response.status}). Saving locally.`, 'error', 5000);
        saveBatchLocally(batchPayload);
      }
    } catch (err) {
      console.error(`Network error: ${err.message}`);
      showToast(`Could not reach server. Saving locally.`, 'error', 5000);
      saveBatchLocally(batchPayload);
    }
  };

  return (
    <Container fluid className="test-dashboard">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast-bar toast-${toast.type} toast-fixed`}>
          <span className="toast-message">
            {toast.message}
            {toast.links && toast.links.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" className="toast-link">
                {link.label}
              </a>
            ))}
          </span>
          <button className="toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* Header */}
      <Row className="dashboard-header">
        <Col>
          <h1 className="dashboard-title">संधि-विकृति-परीक्षा</h1>
          <p className="dashboard-subtitle">
            <code style={{ background: '#e3f2fd', padding: '2px 8px', borderRadius: '4px', marginRight: '10px' }}>
              /sandhi-vikruti-test
            </code>
            Vikruti (Krama/Jata) API Testing
          </p>
        </Col>
      </Row>

      {/* Server Selection */}
      <Row className="server-row">
        <Col>
          <div className="server-selector">
            <label className="server-label">Server URL:</label>
            <select
              value={serverUrl === DEFAULT_SERVERS.localhost || serverUrl === DEFAULT_SERVERS.production ? serverUrl : 'custom'}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setCustomServerUrl(serverUrl);
                } else {
                  handleServerChange(e.target.value);
                }
              }}
              className="server-select"
            >
              <option value={DEFAULT_SERVERS.localhost}>Localhost (8000)</option>
              <option value={DEFAULT_SERVERS.production}>Production</option>
              <option value="custom">Custom...</option>
            </select>
            {serverUrl !== DEFAULT_SERVERS.localhost && serverUrl !== DEFAULT_SERVERS.production && (
              <input
                type="text"
                value={customServerUrl || serverUrl}
                onChange={(e) => setCustomServerUrl(e.target.value)}
                onBlur={() => customServerUrl && handleServerChange(customServerUrl)}
                placeholder="Enter custom server URL"
                className="server-custom-input"
              />
            )}
            <span className={`server-status ${serverUrl.includes('localhost') ? 'local' : 'remote'}`}>
              {serverUrl.includes('localhost') ? 'Local' : 'Remote'}
            </span>
          </div>
        </Col>
      </Row>

      {/* Storage Path & Serial Info */}
      <Row style={{ marginBottom: '10px' }}>
        <Col>
          <div style={{
            background: '#e8f5e9',
            border: '1px solid #a5d6a7',
            borderRadius: '6px',
            padding: '10px 15px',
            fontSize: '0.85rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '15px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>Storage:</span>
              <code style={{ background: '#c8e6c9', padding: '2px 6px', borderRadius: '3px', fontSize: '0.8rem' }}>
                vedictools-server/docs/test_results
              </code>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ fontWeight: 'bold', color: '#1565c0' }}>Next Serial:</span>
              <span style={{ background: '#bbdefb', padding: '2px 8px', borderRadius: '3px', fontFamily: 'monospace' }}>
                #{getCurrentBatchNumber() + 1}
              </span>
            </div>
            {lastSubmission && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Last Sent:</span>
                  <span style={{ background: '#e1bee7', padding: '2px 8px', borderRadius: '3px' }}>
                    #{lastSubmission.serial} ({lastSubmission.reportCount} reports)
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontWeight: 'bold', color: '#f57c00' }}>Date:</span>
                  <span style={{ background: '#ffe0b2', padding: '2px 8px', borderRadius: '3px' }}>
                    {lastSubmission.date}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ fontWeight: 'bold', color: '#00796b' }}>Time:</span>
                  <span style={{ background: '#b2dfdb', padding: '2px 8px', borderRadius: '3px' }}>
                    {lastSubmission.time}
                  </span>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row className="actions-row">
        <Col>
          <div className="action-buttons">
            <Button
              variant="primary"
              onClick={processAllTestCases}
              disabled={loading}
              className="process-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Processing...
                </>
              ) : (
                'Process All'
              )}
            </Button>
            <Button variant="success" onClick={sendAllToServer} className="send-btn">
              Save All to Server
            </Button>
            <Button variant="outline-secondary" onClick={addTestCase} className="add-btn">
              + Add Row
            </Button>
            <Button variant="outline-info" onClick={exportTestCases} className="export-btn">
              Export JSON
            </Button>
            <label className="import-btn btn btn-outline-info">
              Import JSON
              <input type="file" accept=".json" onChange={importTestCases} hidden />
            </label>
            <Button variant="outline-danger" onClick={clearAll} className="clear-btn">
              Clear All
            </Button>
          </div>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Row className="tab-row">
        <Col>
          <div className="tab-buttons">
            <button
              className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Tests
              <span className="tab-badge all-badge">{testCases.length}</span>
            </button>
            <button
              className={`tab-btn failed-tab ${activeTab === 'failed' ? 'active' : ''}`}
              onClick={() => setActiveTab('failed')}
            >
              Failed Tests
              <span className="tab-badge failed-badge">{failedTestCases.length}</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              Report
              <span className="tab-badge all-badge">{submittedTestCases.length}</span>
            </button>
          </div>
        </Col>
      </Row>

      {/* Test Cases Table */}
      <Row className="table-row">
        <Col>
          <div className="test-table-container">
            <table className="test-table test-table-rows">
              <thead>
                <tr>
                  <th className="col-sl">Sl</th>
                  <th className="col-user">User</th>
                  <th className="col-input">Input (Pada Samuha)</th>
                  <th style={{ width: '100px' }}>Options</th>
                  <th className="col-process">Run</th>
                  <th className="col-output">Krama</th>
                  <th style={{ width: '60px' }}>OK?</th>
                  <th className="col-output">Jata</th>
                  <th style={{ width: '60px' }}>OK?</th>
                  <th className="col-note">Note</th>
                  <th className="col-submit">Submit</th>
                </tr>
              </thead>
              <tbody>
                {filteredTestCases.map((tc) => (
                  <React.Fragment key={tc.id}>
                    <tr className={`${processingId === tc.id ? 'processing-row' : ''} ${tc.kramaOk === 'yes' && tc.jataOk === 'yes' ? 'all-ok-row' : ''}`}>
                      <td className="cell-sl">{tc.slNo}</td>

                      <td className="cell-user">
                        <input
                          type="text"
                          value={tc.userName}
                          onChange={(e) => updateTestCase(tc.id, 'userName', e.target.value)}
                          placeholder="Name"
                          className="user-input"
                        />
                      </td>

                      <td className="cell-input">
                        <textarea
                          value={tc.input}
                          onChange={(e) => updateTestCase(tc.id, 'input', e.target.value)}
                          placeholder="Enter Pada Samuha..."
                          className="test-input"
                          disabled={processingId === tc.id}
                        />
                      </td>

                      <td style={{ background: '#f5f5f5', verticalAlign: 'top', padding: '6px' }}>
                        <select
                          value={tc.vedaShakha}
                          onChange={(e) => updateTestCase(tc.id, 'vedaShakha', e.target.value)}
                          style={{ width: '100%', padding: '4px', fontSize: '0.75rem', borderRadius: '4px', border: '1px solid #cfd8dc' }}
                        >
                          {SHAKHA_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.value}</option>
                          ))}
                        </select>
                        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <input
                              type="checkbox"
                              checked={tc.showUdattaPrachaya}
                              onChange={(e) => updateTestCase(tc.id, 'showUdattaPrachaya', e.target.checked)}
                            />
                            उ-प्र
                          </label>
                          <label style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <input
                              type="checkbox"
                              checked={tc.apadaantam}
                              onChange={(e) => updateTestCase(tc.id, 'apadaantam', e.target.checked)}
                            />
                            अपदा
                          </label>
                        </div>
                      </td>

                      <td className="cell-process">
                        <button
                          className="row-process-btn"
                          onClick={() => processSingleRow(tc)}
                          disabled={loading || processingId === tc.id || !tc.input.trim()}
                          title="Process this row"
                        >
                          {processingId === tc.id ? '...' : '\u25B6'}
                        </button>
                      </td>

                      {/* Krama */}
                      <td className={`cell-output ${tc.kramaOk === 'yes' ? 'output-yes' : tc.kramaOk === 'no' ? 'output-no' : ''}`}>
                        <textarea
                          value={tc.kramaOutput}
                          readOnly
                          placeholder={processingId === tc.id ? 'Loading...' : 'Krama...'}
                          className="test-output"
                          style={{ fontSize: '0.8rem' }}
                        />
                        {tc.kramaOk === 'no' && (
                          <textarea
                            value={tc.kramaCorrection || ''}
                            onChange={(e) => updateTestCase(tc.id, 'kramaCorrection', e.target.value)}
                            placeholder="Expected..."
                            className="test-correction"
                            style={{ fontSize: '0.75rem', marginTop: '4px' }}
                          />
                        )}
                      </td>

                      <td className="cell-validation">
                        <div className="yes-no-buttons" style={{ flexDirection: 'column' }}>
                          <button
                            className={`yn-btn yes-btn ${tc.kramaOk === 'yes' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'kramaOk', 'yes')}
                            disabled={!tc.kramaOutput || tc.kramaOutput.startsWith('Error')}
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            Y
                          </button>
                          <button
                            className={`yn-btn no-btn ${tc.kramaOk === 'no' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'kramaOk', 'no')}
                            disabled={!tc.kramaOutput || tc.kramaOutput.startsWith('Error')}
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            N
                          </button>
                        </div>
                      </td>

                      {/* Jata */}
                      <td className={`cell-output ${tc.jataOk === 'yes' ? 'output-yes' : tc.jataOk === 'no' ? 'output-no' : ''}`}>
                        <textarea
                          value={tc.jataOutput}
                          readOnly
                          placeholder={processingId === tc.id ? 'Loading...' : 'Jata...'}
                          className="test-output"
                          style={{ fontSize: '0.8rem' }}
                        />
                        {tc.jataOk === 'no' && (
                          <textarea
                            value={tc.jataCorrection || ''}
                            onChange={(e) => updateTestCase(tc.id, 'jataCorrection', e.target.value)}
                            placeholder="Expected..."
                            className="test-correction"
                            style={{ fontSize: '0.75rem', marginTop: '4px' }}
                          />
                        )}
                      </td>

                      <td className="cell-validation">
                        <div className="yes-no-buttons" style={{ flexDirection: 'column' }}>
                          <button
                            className={`yn-btn yes-btn ${tc.jataOk === 'yes' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'jataOk', 'yes')}
                            disabled={!tc.jataOutput || tc.jataOutput.startsWith('Error')}
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            Y
                          </button>
                          <button
                            className={`yn-btn no-btn ${tc.jataOk === 'no' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'jataOk', 'no')}
                            disabled={!tc.jataOutput || tc.jataOutput.startsWith('Error')}
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                          >
                            N
                          </button>
                        </div>
                      </td>

                      <td className="cell-note">
                        <button
                          className={`note-toggle-btn ${tc.scholarComment ? 'has-note' : ''}`}
                          onClick={() => handleScholarNote(tc)}
                          title={tc.scholarComment || 'Add note'}
                        >
                          {showNoteModal === tc.id ? '\u2713' : tc.scholarComment ? '\uD83D\uDCDD' : '+'}
                        </button>
                      </td>

                      <td className="cell-submit">
                        {tc.submitted ? (
                          <span className="submitted-badge" title="Submitted">{'\u2713'}</span>
                        ) : (
                          <button
                            className="submit-btn"
                            onClick={() => submitTestCase(tc)}
                            disabled={!tc.kramaOutput && !tc.jataOutput}
                            title="Submit"
                            style={{ fontSize: '0.7rem', padding: '4px 8px' }}
                          >
                            OK
                          </button>
                        )}
                      </td>
                    </tr>

                    {lastSubmittedId === tc.id && (
                      <tr className="submit-confirm-row">
                        <td colSpan="11" className="submit-confirm-cell">
                          <div className="submit-confirm-container">
                            <span className="submit-confirm-text">
                              Thanks! Add another?
                            </span>
                            <button className="submit-confirm-yes" onClick={addRowAfterSubmit}>
                              Yes
                            </button>
                            <button className="submit-confirm-no" onClick={dismissSubmitConfirm}>
                              No
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {showNoteModal === tc.id && (
                      <tr className="note-row">
                        <td colSpan="11" className="note-cell">
                          <div className="note-container">
                            <label>Scholar Note:</label>
                            <textarea
                              value={tc.scholarComment}
                              onChange={(e) => updateTestCase(tc.id, 'scholarComment', e.target.value)}
                              placeholder="Enter scholar comment or note..."
                              className="scholar-note-input"
                              autoFocus
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>

      {/* Summary */}
      <Row className="info-row">
        <Col>
          {activeTab === 'failed' ? (
            <div className="info-panel failed-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span className="info-text">
                  Failed: {failedTestCases.length} |
                  Krama: {failedTestCases.filter(tc => tc.kramaOk === 'no').length} |
                  Jata: {failedTestCases.filter(tc => tc.jataOk === 'no').length}
                </span>
                <Button
                  variant="danger"
                  onClick={sendAllToServer}
                  disabled={failedTestCases.length === 0}
                  size="sm"
                >
                  Save Failed to Server
                </Button>
              </div>
            </div>
          ) : activeTab === 'report' ? (
            <div className="info-panel report-panel">
              <div className="report-summary">
                <h5 className="report-title">Test Report Summary</h5>
                <div className="report-stats">
                  <div className="report-stat">
                    <span className="stat-label">Total Submitted:</span>
                    <span className="stat-value">{submittedTestCases.length}</span>
                  </div>
                  <div className="report-stat passed">
                    <span className="stat-label">Krama OK:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.kramaOk === 'yes').length}</span>
                  </div>
                  <div className="report-stat passed">
                    <span className="stat-label">Jata OK:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.jataOk === 'yes').length}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="info-panel">
              <span className="info-text">
                Total: {testCases.length} |
                Krama: {testCases.filter(tc => tc.kramaOk === 'yes').length}Y/{testCases.filter(tc => tc.kramaOk === 'no').length}N |
                Jata: {testCases.filter(tc => tc.jataOk === 'yes').length}Y/{testCases.filter(tc => tc.jataOk === 'no').length}N
              </span>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SandhiVikrutiTest;
